import React, {
  useContext, useEffect, useState, useRef,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

import type { AuthenticatedUser } from '../../../types';
import { SpinLoader } from '../../../components';
import { useHeaderFooter } from '../../../hooks';
import { deleteCookie } from '../../../utils';
import { ControllerFieldInput, ControllerFiledPassword, FooterLinks } from '../components';
import { getRedirectToUrl } from '../utils';
import LoginNotification from './LoginNotification';
import { validateFormFields } from './utils';
import {
  LoginProps,
  FieldErrorsTypes,
  FieldTypes,
  ErrorsTypes,
} from './types';
import {
  INVALID_FORM,
  LOGIN_INITIAL_FORM_STATE,
  LOGIN_INITIAL_CODE_STATE,
} from './constants';

import '../Logistration.scss';
import logistrationMessages from '../messages';
import messages from './messages';

const Login = ({ children }: LoginProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrorsTypes>({});
  const [notificationCode, setNotificationCode] = useState<ErrorsTypes>(LOGIN_INITIAL_CODE_STATE);
  const [formFields, setFormFields] = useState<FieldTypes>(LOGIN_INITIAL_FORM_STATE);

  const intl = useIntl();
  const { authenticatedUser } = useContext<{ authenticatedUser: AuthenticatedUser | null }>(AppContext);
  const { headerContent } = useHeaderFooter(children);
  const redirectToUrl = getRedirectToUrl();
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { code?: string } };
  const hasProcessedStateCodeRef = useRef(false);

  useEffect(() => {
    if (authenticatedUser) {
      window.location.replace(redirectToUrl);
    }
  }, [authenticatedUser]);

  useEffect(() => {
    if (hasProcessedStateCodeRef.current) {
      return;
    }

    const code = state?.code;
    if (code) {
      hasProcessedStateCodeRef.current = true;
      setNotificationCode(prevState => ({ type: code, count: prevState.count + 1, context: {} }));
      navigate('.', { replace: true, state: null });
    }
  }, [state, navigate]);

  if (authenticatedUser) {
    return null;
  }

  const loginRequest = async (formData: FormData) => {
    const requestConfig = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      isPublic: true,
    };

    try {
      setIsSubmitting(true);
      const { data } = await getAuthenticatedHttpClient()
        .post(
          `${getConfig().LMS_BASE_URL}/api/user/v2/account/login_session/`,
          formData,
          requestConfig,
        )
        .catch((e: unknown) => {
          throw (e);
        });

      return {
        redirectUrl: data?.redirect_url || '',
        success: data?.success || false,
      };
    } catch (error) {
      setIsSubmitting(false);
      if (error) {
        const {
          error_code: code,
          context,
          email,
        } = JSON.parse((error as any).customAttributes.httpErrorResponseData) ?? {};
        setNotificationCode(prevState => ({
          type: code,
          count: prevState.count + 1,
          context: { ...(context ?? {}), email },
        }));
      }
      return null;
    }
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const emailOrUsername = formFields.email_or_username.trim();
    const { password } = formFields;
    const validationErrors = validateFormFields({ email_or_username: emailOrUsername, password }, intl);

    if (validationErrors.email_or_username || validationErrors.password) {
      setErrors(validationErrors);
      setNotificationCode(prevState => ({ type: INVALID_FORM, count: prevState.count + 1, context: {} }));
      return;
    }

    // Clear the language preference cookie to ensure the user's browser default language is used
    // This is done before login to prevent any potential language preference conflicts
    // The cookie will be set again based on user preferences after successful authentication
    deleteCookie('openedx-language-preference', { path: '/' });

    const formData = new FormData();

    formData.append('email_or_username', emailOrUsername);
    formData.append('password', password);
    formData.append('next', redirectToUrl || window.location.origin);

    const { redirectUrl, success } = await loginRequest(formData) ?? {};
    if (success && redirectUrl) {
      window.location.replace(decodeURIComponent(redirectToUrl));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormFields(prevState => ({ ...prevState, [name]: value }));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
  };

  return (
    <div className="container-center-horizontal">
      <div className="logistration-main screen">
        {headerContent}
        <div className="logistration">
          <div className="auth-page-block">
            <div className="auth-page-main-content">
              <LoginNotification
                code={notificationCode.type}
                count={notificationCode.count}
                context={notificationCode.context}
              />
              <h2 className="auth-page-title">
                {intl.formatMessage(messages.title)}
              </h2>
              {getConfig().SHOW_RESET_PASSWORD_ALERT && (
                <div className="auth-alert auth-alert-warning">
                  {intl.formatMessage(messages.resetPasswordNotificationMessage)}
                </div>
              )}
              <form className="auth-form">
                <ControllerFieldInput
                  name="email_or_username"
                  placeholder={intl.formatMessage(messages.fieldEmailAndUsernamePlaceholder)}
                  value={formFields.email_or_username}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.email_or_username}
                />
                <ControllerFiledPassword
                  name="password"
                  placeholder={intl.formatMessage(messages.password)}
                  value={formFields.password}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.password}
                >
                  <Link
                    className="auth-link-redirect"
                    to={`/forgot-password?redirect-to=${redirectToUrl}`}
                  >
                    {intl.formatMessage(messages.forgotPassword)}
                  </Link>
                </ControllerFiledPassword>
                <button
                  className="auth-submit-btn"
                  type="submit"
                  aria-label={intl.formatMessage(logistrationMessages.submitButtonText)}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      {intl.formatMessage(logistrationMessages.submitLoadingButtonText)}
                      <SpinLoader size={20} color="#fff" />
                    </>
                  ) : intl.formatMessage(logistrationMessages.submitButtonText)}
                </button>
              </form>
              <div className="auth-redirect-block">
                {intl.formatMessage(messages.noAccount)}
                {' '}
                <Link
                  className="auth-link-redirect"
                  to={`/signup?redirect-to=${redirectToUrl}`}
                >
                  {intl.formatMessage(messages.signUp)}
                </Link>
              </div>
            </div>
            <FooterLinks />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
